'use client';

import { c as _c } from "react/compiler-runtime";
import { useReducedMotion } from 'motion/react';
import { type RefObject, useEffect, useRef } from 'react';
import { useMediaQuery } from '@/app/hooks/useMediaQuery';
import type { MutableRef } from '@/app/lcv/components/sections/spartan/shared/webgpu/canvas-basic/types';
const RESIZE_DEBOUNCE_MS = 120;
export interface PointerPosition {
  x: number;
  y: number;
  isInside: boolean;
}
export const createPointerPosition = (): PointerPosition => ({
  x: 0,
  y: 0,
  isInside: false
});
interface UsePointerPositionOptions {
  elementRef: RefObject<HTMLElement | null>;
  targetRef: MutableRef<PointerPosition>;
  enabled: boolean;
}
export const usePointerPosition = t0 => {
  const $ = _c(6);
  const {
    elementRef,
    targetRef,
    enabled
  } = t0;
  const isDesktopPointer = useMediaQuery("(hover: hover) and (pointer: fine)") ?? false;
  const isReducedMotion = useReducedMotion();
  const targetWriteRef = useRef(targetRef);
  let t1;
  let t2;
  if ($[0] !== elementRef || $[1] !== enabled || $[2] !== isDesktopPointer || $[3] !== isReducedMotion) {
    t1 = () => {
      const element = elementRef.current;
      const written = targetWriteRef.current;
      if (!element) {
        return;
      }
      if (!enabled || !isDesktopPointer || isReducedMotion) {
        written.value.isInside = false;
        return;
      }
      let rect = element.getBoundingClientRect();
      const measure = () => {
        rect = element.getBoundingClientRect();
      };
      const handlePointerMove = event => {
        if (rect.width <= 0 || rect.height <= 0) {
          return;
        }
        written.value.x = (event.clientX - rect.left) / rect.width;
        written.value.y = 1 - (event.clientY - rect.top) / rect.height;
        written.value.isInside = true;
      };
      const handlePointerLeave = () => {
        written.value.isInside = false;
      };
      element.addEventListener("pointermove", handlePointerMove);
      element.addEventListener("pointerleave", handlePointerLeave);
      const observer = new ResizeObserver(measure);
      observer.observe(element);
      let resizeTimer;
      const handleResize = () => {
        window.clearTimeout(resizeTimer);
        resizeTimer = window.setTimeout(measure, RESIZE_DEBOUNCE_MS);
      };
      window.addEventListener("resize", handleResize);
      window.addEventListener("scroll", handleResize, {
        passive: true
      });
      return () => {
        element.removeEventListener("pointermove", handlePointerMove);
        element.removeEventListener("pointerleave", handlePointerLeave);
        observer.disconnect();
        window.clearTimeout(resizeTimer);
        window.removeEventListener("resize", handleResize);
        window.removeEventListener("scroll", handleResize);
      };
    };
    t2 = [elementRef, enabled, isDesktopPointer, isReducedMotion];
    $[0] = elementRef;
    $[1] = enabled;
    $[2] = isDesktopPointer;
    $[3] = isReducedMotion;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
};